/*
 * KYNDLE PREMIUM COLLECTION
 * Dual-handle price range slider that stays in sync with Dawn's native
 * price facet text inputs (Nuo / Iki). It does not implement its own
 * AJAX or URL logic — it only updates the existing inputs' values and
 * dispatches native `input`/`change` events so assets/facets.js (Dawn's
 * own FacetFiltersForm) picks up the change through its existing
 * debounced listener. Vanilla JS, no dependencies, deferred.
 */
(() => {
  function toNumber(raw) {
    if (raw === null || raw === undefined || raw === '') return null;
    const normalized = String(raw).trim().replace(',', '.');
    const value = parseFloat(normalized);
    return Number.isFinite(value) ? value : null;
  }

  class KyndlePriceSlider extends HTMLElement {
    connectedCallback() {
      this.rangeMin = toNumber(this.dataset.rangeMin) ?? 0;
      this.rangeMax = toNumber(this.dataset.rangeMax) ?? 100;
      if (this.rangeMax <= this.rangeMin) this.rangeMax = this.rangeMin + 1;
      this.rangeMax = Math.ceil(this.rangeMax);

      this.minHandle = this.querySelector('.kyndle-price-slider__handle--min');
      this.maxHandle = this.querySelector('.kyndle-price-slider__handle--max');
      this.fill = this.querySelector('.kyndle-price-slider__range');

      const priceRange = this.closest('price-range');
      const textInputs = priceRange ? priceRange.querySelectorAll('input[type="text"]') : [];
      this.minTextInput = textInputs[0] || null;
      this.maxTextInput = textInputs[1] || null;
      if (!this.minHandle || !this.maxHandle || !this.fill || !this.minTextInput || !this.maxTextInput) return;

      const currentMin = toNumber(this.dataset.currentMin);
      const currentMax = toNumber(this.dataset.currentMax);

      [this.minHandle, this.maxHandle].forEach((handle) => {
        handle.min = String(this.rangeMin);
        handle.max = String(this.rangeMax);
        handle.step = '1';
      });

      this.minHandle.value = String(Math.round(currentMin ?? this.rangeMin));
      this.maxHandle.value = String(Math.round(currentMax ?? this.rangeMax));

      this.paint();

      this.minHandle.addEventListener('input', () => this.onHandleInput('min'));
      this.maxHandle.addEventListener('input', () => this.onHandleInput('max'));
      this.minHandle.addEventListener('change', () => this.commit());
      this.maxHandle.addEventListener('change', () => this.commit());

      // Keep the slider in sync if the person types directly into Nuo/Iki.
      this.minTextInput.addEventListener('change', () => this.syncFromTextInputs());
      this.maxTextInput.addEventListener('change', () => this.syncFromTextInputs());
    }

    onHandleInput(which) {
      let min = Number(this.minHandle.value);
      let max = Number(this.maxHandle.value);

      if (min > max) {
        if (which === 'min') {
          max = min;
          this.maxHandle.value = String(max);
        } else {
          min = max;
          this.minHandle.value = String(min);
        }
      }

      this.paint();
      this.writeToTextInput(this.minTextInput, min, this.rangeMin);
      this.writeToTextInput(this.maxTextInput, max, this.rangeMax);
    }

    commit() {
      // Fire `change` so Dawn's native PriceRange element re-validates and
      // the debounced FacetFiltersForm submit request goes out.
      this.minTextInput.dispatchEvent(new Event('change', { bubbles: true }));
      this.maxTextInput.dispatchEvent(new Event('change', { bubbles: true }));
    }

    writeToTextInput(input, value, boundaryValue) {
      const nextValue = value === boundaryValue ? '' : String(Math.round(value));
      if (input.value === nextValue) return;
      input.value = nextValue;
      input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    syncFromTextInputs() {
      const min = toNumber(this.minTextInput.value) ?? this.rangeMin;
      const max = toNumber(this.maxTextInput.value) ?? this.rangeMax;
      this.minHandle.value = String(Math.round(Math.min(Math.max(min, this.rangeMin), this.rangeMax)));
      this.maxHandle.value = String(Math.round(Math.min(Math.max(max, this.rangeMin), this.rangeMax)));
      this.paint();
    }

    paint() {
      const span = this.rangeMax - this.rangeMin || 1;
      const min = Number(this.minHandle.value);
      const max = Number(this.maxHandle.value);
      const left = ((min - this.rangeMin) / span) * 100;
      const right = ((max - this.rangeMin) / span) * 100;
      this.fill.style.left = `${left}%`;
      this.fill.style.right = `${100 - right}%`;
    }
  }

  if (!customElements.get('kyndle-price-slider')) {
    customElements.define('kyndle-price-slider', KyndlePriceSlider);
  }
})();
